import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useApplyToursPricingSuggestion } from '~/hooks/apis/lere/useApplyToursPricingSuggestion';
import { useGetLereToursPricingSuggestions } from '~/hooks/apis/lere/useGetLereToursPricingSuggestions';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import ToursCapacitySalesFormatter from './formatters/ToursCapacitySalesFormatter';
import ToursCurrentPricingFormatter from './formatters/ToursCurrentPricingFormatter';
import ToursIdsFormatter from './formatters/ToursIdsFormatter';
import ToursPricingFormatter from './formatters/ToursPricingFormatter';
import ToursScheduleFormatter from './formatters/ToursScheduleFormatter';
import {
  SUGGESTED_ACTION_TYPE_OPTIONS,
  capitalizeFirst,
  formatCurrency,
  formatDate,
  formatSignedCurrency,
  formatSignedPercent,
  getChangeColor,
  getTourOfferHref,
} from './formatters/toursPricingUtils';
import { TOURS_PRICING_SAMPLE_DATA } from './toursPricingSampleData';
import {
  LerePaginatedResponse,
  LereTabComponentProps,
  LereToursPricingApplyRequest,
  LereToursPricingSuggestion,
  LereToursPricingSuggestionsParams,
  SuggestedActionType,
  ToursPricingApplyStatus,
} from './types';

const EMPTY_RESPONSE: LerePaginatedResponse<LereToursPricingSuggestion> = {
  result: [],
  total: 0,
};

type AppliedFilters = Omit<LereToursPricingSuggestionsParams, 'page' | 'limit'>;
type AppliedOverride = Pick<
  LereToursPricingApplyRequest,
  'applied_action_type' | 'applied_price_change_percent' | 'applied_after_price'
>;

type ValidatedAppliedPercent = {
  value: number | null;
  error: string | null;
};

type AppliedPreview = AppliedOverride & {
  netVsCurrentAmount: number;
  netVsCurrentPercent: number | null;
};

const APPLIED_PERCENT_MIN = -100;
const APPLIED_PERCENT_MAX = 100;

function roundCurrencyValue(value: number): number {
  return Math.round(value * 100) / 100;
}

function getActionTypeForPercent(priceChangePercent: number): SuggestedActionType {
  return priceChangePercent >= 0 ? 'surcharge' : 'discount';
}

function getPercentInputValue(priceChangePercent: number): string {
  return Number((priceChangePercent * 100).toFixed(4)).toString();
}

function getSignedValueColor(value: number | null): string {
  if (value == null || value === 0) {
    return 'text.primary';
  }
  return getChangeColor(getActionTypeForPercent(value));
}

function buildAppliedPreview(row: LereToursPricingSuggestion, appliedPriceChangePercent: number): AppliedPreview {
  const currentLivePrice = row.current_tour_price ?? row.original_tour_price;
  const appliedAfterPrice = roundCurrencyValue(row.original_tour_price * (1 + appliedPriceChangePercent));
  const netVsCurrentAmount = roundCurrencyValue(appliedAfterPrice - currentLivePrice);
  const netVsCurrentPercent = currentLivePrice !== 0 ? netVsCurrentAmount / currentLivePrice : null;

  return {
    applied_action_type: getActionTypeForPercent(appliedPriceChangePercent),
    applied_price_change_percent: appliedPriceChangePercent,
    applied_after_price: appliedAfterPrice,
    netVsCurrentAmount,
    netVsCurrentPercent,
  };
}

function validateAppliedPercentInput(input: string): ValidatedAppliedPercent {
  const trimmedInput = input.trim();
  if (!trimmedInput) {
    return { value: null, error: 'Price change percent is required.' };
  }

  const parsedPercent = Number(trimmedInput);
  if (!Number.isFinite(parsedPercent)) {
    return { value: null, error: 'Enter a valid number.' };
  }

  if (parsedPercent < APPLIED_PERCENT_MIN || parsedPercent > APPLIED_PERCENT_MAX) {
    return {
      value: null,
      error: `Price change percent must be between ${APPLIED_PERCENT_MIN} and ${APPLIED_PERCENT_MAX}.`,
    };
  }

  return { value: parsedPercent / 100, error: null };
}

function buildApplyRequest(
  row: LereToursPricingSuggestion,
  status: ToursPricingApplyStatus,
  appliedOverride?: AppliedOverride,
): LereToursPricingApplyRequest {
  const defaultAppliedPriceChangePercent = row.suggested_price_change_percent;

  return {
    suggestion_id: row.suggestion_id,
    status,
    offer_bk: row.offer_bk,
    offer_salesforce_bk: row.offer_salesforce_bk,
    offer_name: row.offer_name,
    departure_date: row.departure_date,
    departure_name: row.departure_name,
    suggested_action_type: row.suggested_action_type,
    distance_category: row.distance_category,
    original_tour_price: row.original_tour_price,
    suggested_price_change_percent: row.suggested_price_change_percent,
    suggested_after_price: row.after_price,
    applied_action_type:
      appliedOverride?.applied_action_type ?? getActionTypeForPercent(defaultAppliedPriceChangePercent),
    applied_price_change_percent: appliedOverride?.applied_price_change_percent ?? defaultAppliedPriceChangePercent,
    applied_after_price: appliedOverride?.applied_after_price ?? row.after_price,
    total_pax_sold: row.total_pax_sold,
    max_pax: row.max_pax,
    leadtime: row.leadtime,
  };
}

export default function RMToursPricingSuggestions({ onLereError }: LereTabComponentProps) {
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 20 });
  const {
    mutate: applyPricingSuggestion,
    isPending: isApplying,
    variables: applyingVariables,
  } = useApplyToursPricingSuggestion();

  const [offerBkInput, setOfferBkInput] = useState('');
  const [salesforceIdInput, setSalesforceIdInput] = useState('');
  const [actionTypeInput, setActionTypeInput] = useState<SuggestedActionType | ''>('');
  const [useLiveData, setUseLiveData] = useState(true);
  const [applyDialogRow, setApplyDialogRow] = useState<LereToursPricingSuggestion | null>(null);
  const [appliedPercentInput, setAppliedPercentInput] = useState('');

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  const queryParams = useMemo<LereToursPricingSuggestionsParams>(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [paginationModel.page, paginationModel.pageSize, appliedFilters],
  );

  const toursPricingSuggestionsQuery = useGetLereToursPricingSuggestions(queryParams, { enabled: useLiveData });

  const samplePaginatedData = useMemo<LerePaginatedResponse<LereToursPricingSuggestion>>(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return {
      result: TOURS_PRICING_SAMPLE_DATA.slice(start, end),
      total: TOURS_PRICING_SAMPLE_DATA.length,
    };
  }, [paginationModel.page, paginationModel.pageSize]);

  const toursPricingSuggestionsData = useLiveData
    ? toursPricingSuggestionsQuery.data ?? EMPTY_RESPONSE
    : samplePaginatedData;

  useEffect(() => {
    if (toursPricingSuggestionsQuery.error) {
      onLereError(toursPricingSuggestionsQuery.error);
    }
  }, [toursPricingSuggestionsQuery.error, onLereError]);

  const handleSearch = () => {
    setAppliedFilters({
      offer_bk: offerBkInput || undefined,
      offer_salesforce_bk: salesforceIdInput || undefined,
      suggested_action_type: actionTypeInput || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setOfferBkInput('');
    setSalesforceIdInput('');
    setActionTypeInput('');
    setAppliedFilters({});
    setPage(0);
  };

  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setActionTypeInput(event.target.value as SuggestedActionType | '');
  };

  const closeApplyDialog = useCallback(() => {
    setApplyDialogRow(null);
    setAppliedPercentInput('');
  }, []);

  const openApplyDialog = useCallback((row: LereToursPricingSuggestion) => {
    setApplyDialogRow(row);
    setAppliedPercentInput(getPercentInputValue(row.suggested_price_change_percent));
  }, []);

  const handleAction = useCallback(
    (row: LereToursPricingSuggestion, status: ToursPricingApplyStatus) => {
      if (status === 'accepted') {
        openApplyDialog(row);
        return;
      }

      applyPricingSuggestion(buildApplyRequest(row, status));
    },
    [openApplyDialog, applyPricingSuggestion],
  );

  const appliedPercentValidation = useMemo(
    () => validateAppliedPercentInput(appliedPercentInput),
    [appliedPercentInput],
  );

  const applyDialogPreview = useMemo(() => {
    if (!applyDialogRow || appliedPercentValidation.value == null) {
      return null;
    }

    return buildAppliedPreview(applyDialogRow, appliedPercentValidation.value);
  }, [applyDialogRow, appliedPercentValidation.value]);
  const handleApplyDialogConfirm = useCallback(() => {
    if (!applyDialogRow) {
      return;
    }

    const { value: appliedPriceChangePercent, error } = appliedPercentValidation;
    if (error || appliedPriceChangePercent == null) {
      return;
    }

    const appliedPreview = buildAppliedPreview(applyDialogRow, appliedPriceChangePercent);
    applyPricingSuggestion(buildApplyRequest(applyDialogRow, 'accepted', appliedPreview), {
      onSuccess: closeApplyDialog,
    });
  }, [applyDialogRow, appliedPercentValidation, applyPricingSuggestion, closeApplyDialog]);

  const pendingSuggestionId = isApplying ? applyingVariables?.suggestion_id : null;

  const columns = useMemo<Array<GridColDef<LereToursPricingSuggestion>>>(() => {
    const compactCol: Partial<GridColDef<LereToursPricingSuggestion>> = {
      minWidth: 120,
      flex: 1,
    };

    return [
      {
        ...compactCol,
        field: 'offer_name',
        headerName: 'Offer Name',
        minWidth: 220,
        flex: 2,
        renderCell: ({ row }) => {
          const name = row.offer_name || '—';
          const offerHref = getTourOfferHref(row.offer_bk, row.offer_url);
          if (offerHref && name !== '—') {
            return (
              <Link href={offerHref} target="_blank" rel="noopener noreferrer" underline="hover">
                {name}
              </Link>
            );
          }
          return name;
        },
      },
      {
        ...compactCol,
        field: 'departure_date',
        headerName: 'Departure Date',
        minWidth: 135,
        renderCell: ({ row }) => formatDate(row.departure_date),
      },
      {
        field: 'ids',
        headerName: 'IDs',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursIdsFormatter,
      },
      {
        ...compactCol,
        field: 'suggested_action_type',
        headerName: 'Action Type',
        renderCell: ({ row }) => capitalizeFirst(row.suggested_action_type),
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursScheduleFormatter,
      },
      {
        field: 'current_pricing',
        headerName: 'Current Pricing',
        display: 'flex',
        flex: 2,
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: ToursCurrentPricingFormatter,
      },
      {
        field: 'proposed_change',
        headerName: 'Proposed Change',
        display: 'flex',
        flex: 2,
        minWidth: 240,
        sortable: false,
        filterable: false,
        renderCell: ToursPricingFormatter,
      },
      {
        field: 'capacity_sales',
        headerName: 'Capacity & Sales',
        display: 'flex',
        flex: 2,
        minWidth: 240,
        sortable: false,
        filterable: false,
        renderCell: ToursCapacitySalesFormatter,
      },
      {
        ...compactCol,
        field: 'offer_country_name',
        headerName: 'Country',
        renderCell: ({ row }) => row.offer_country_name || '—',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        headerClassName: 'sticky-actions-header',
        cellClassName: 'sticky-actions-cell',
        renderCell: ({ row }) => {
          if (pendingSuggestionId === row.suggestion_id) {
            return <CircularProgress size={24} />;
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 1 }}>
              <Tooltip title="Accept & apply pricing change">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleAction(row, 'accepted')}
                  disabled={isApplying}
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject recommendation">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleAction(row, 'rejected')}
                  disabled={isApplying}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ];
  }, [pendingSuggestionId, isApplying, handleAction]);

  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        RM Tours Pricing Suggestions
      </Typography>
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3 }}>
        Filters
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 3 }}>
        <TextField
          label="Offer ID"
          size="small"
          disabled={!useLiveData}
          value={offerBkInput}
          onChange={(e) => setOfferBkInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Salesforce ID"
          size="small"
          disabled={!useLiveData}
          value={salesforceIdInput}
          onChange={(e) => setSalesforceIdInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={!useLiveData}>
          <InputLabel>Action Type</InputLabel>
          <Select value={actionTypeInput} label="Action Type" onChange={handleActionTypeChange}>
            <MenuItem value="">All</MenuItem>
            {SUGGESTED_ACTION_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="text"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleClearFilters}
        >
          Clear filters
        </Button>
        <Button
          variant="contained"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        columns={columns}
        rows={toursPricingSuggestionsData.result}
        rowCount={toursPricingSuggestionsData.total}
        loading={useLiveData && toursPricingSuggestionsQuery.isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={[10, 20, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.suggestion_id}
        slots={{
          pagination: GridPagination,
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        sx={{
          width: '100%',
          '& .sticky-actions-header, & .sticky-actions-cell': {
            position: 'sticky',
            right: 0,
            overflow: 'visible',
            backgroundColor: 'background.paper',
          },
          '& .sticky-actions-header': {
            zIndex: 6,
          },
          '& .sticky-actions-cell': {
            zIndex: 5,
          },
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'transparent',
          },
          // Hide the resize separator on the actions header and the column immediately before it
          '& .sticky-actions-header .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeader:has(+ .sticky-actions-header) .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        }}
      />

      <Dialog
        open={Boolean(applyDialogRow)}
        onClose={closeApplyDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="tours-pricing-apply-dialog-title"
      >
        <DialogTitle id="tours-pricing-apply-dialog-title">Apply pricing change</DialogTitle>
        {applyDialogRow && (
          <>
            <DialogContent>
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  Apply a pricing change for "{applyDialogRow.offer_name}" (departure{' '}
                  {formatDate(applyDialogRow.departure_date)}).
                </Typography>
                <Typography variant="body2">
                  Suggested change: {formatSignedPercent(applyDialogRow.suggested_price_change_percent)} (
                  <Typography
                    component="span"
                    variant="body2"
                    color={getChangeColor(applyDialogRow.suggested_action_type)}
                    fontWeight="medium"
                  >
                    {capitalizeFirst(applyDialogRow.suggested_action_type)}
                  </Typography>
                  ) from {formatCurrency(applyDialogRow.original_tour_price)} to{' '}
                  {formatCurrency(applyDialogRow.after_price)}.
                </Typography>
                <TextField
                  type="number"
                  label="Price change percent (%)"
                  value={appliedPercentInput}
                  onChange={(event) => setAppliedPercentInput(event.target.value)}
                  error={Boolean(appliedPercentValidation.error)}
                  helperText={
                    appliedPercentValidation.error ??
                    `Allowed range: ${APPLIED_PERCENT_MIN}% to ${APPLIED_PERCENT_MAX}%. Example: 3 means +3%.`
                  }
                  slotProps={{
                    htmlInput: { min: APPLIED_PERCENT_MIN, max: APPLIED_PERCENT_MAX, step: 0.1 },
                  }}
                  fullWidth
                />
                {applyDialogPreview && (
                  <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                    <Typography variant="body2">
                      Applied action type:{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color={getChangeColor(applyDialogPreview.applied_action_type)}
                        fontWeight="medium"
                      >
                        {capitalizeFirst(applyDialogPreview.applied_action_type)}
                      </Typography>
                    </Typography>
                    <Typography
                      variant="body2"
                      color={getSignedValueColor(applyDialogPreview.applied_price_change_percent)}
                      fontWeight="medium"
                    >
                      Applied change: {formatSignedPercent(applyDialogPreview.applied_price_change_percent)}
                    </Typography>
                    <Typography variant="body2">
                      Applied live price: {formatCurrency(applyDialogPreview.applied_after_price)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={getSignedValueColor(applyDialogPreview.netVsCurrentAmount)}
                      fontWeight="medium"
                    >
                      Net vs current: {formatSignedCurrency(applyDialogPreview.netVsCurrentAmount)}
                      {applyDialogPreview.netVsCurrentPercent != null && (
                        <> ({formatSignedPercent(applyDialogPreview.netVsCurrentPercent)})</>
                      )}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeApplyDialog} disabled={isApplying}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyDialogConfirm}
                disabled={isApplying || Boolean(appliedPercentValidation.error)}
              >
                Confirm
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={useLiveData}
              onChange={(e) => {
                setUseLiveData(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={
            <Typography variant="body2" color={useLiveData ? 'text.primary' : 'text.secondary'}>
              Use Live Data {!useLiveData && '(currently showing sample data)'}
            </Typography>
          }
        />
      </Box>
    </Box>
  );
}
